import { useMemo, useRef } from "react"
import type { RefObject } from "react"

import { QueryClient, type QueryKey, useQueryClient } from "@tanstack/react-query"
import eq from "lodash-es/eq"
import isEmpty from "lodash-es/isEmpty"

import type { TFeedbackModalProps } from "@/3514/modals"
import { EModalId, type TModalDispatcher, useModalDispatcher } from "@/3514/store/modal"
import { reduxBatch } from "@/3514/utils"
import { type TEmptyCallback } from "@/shared/types/functions"

import {
    useCoachingChatCreateDisclaimerAwarenessMutation as useCreateDisclaimerAwarenessMutation,
    useCoachingChatCreateMatrixFeedbackMutation as useCreateMatrixFeedbackMutation
} from "../api"
import { useCoachingChatConversationDispatcher as useConversationDispatcher } from "../store"
import {
    ECoachingChatConversationStatus as EConversationStatus,
    type ICoachingChatConversationQueryDto as IConversationDto,
    type TCoachingChatGuideModalProps as TGuideModalProps,
    type TCoachingChatMatrixFeedbackModalProps as TMatrixFeedbackModalProps,
    type _TCoachingChatHookSharedParams as _TParams
} from "../types"

type TManager = {
    closeAbandonment: RefObject<TEmptyCallback>
    openGuide: RefObject<(modalProps: Omit<TGuideModalProps, "createMutation">) => void>
    closeGuide: RefObject<TEmptyCallback>
    openFeedback: RefObject<(modalProps: TFeedbackModalProps) => void>
    closeFeedback: RefObject<TEmptyCallback>
    openMatrix: RefObject<TEmptyCallback>
    closeMatrix: RefObject<(conversationQueryKey: QueryKey) => Promise<void>>
}

function useManager({
    entityId
}: Pick<_TParams, "entityId">): ReturnType<(params: Pick<_TParams, "entityId">) => TManager> {
    const queryClient: QueryClient = useQueryClient()

    const modalDispatcher: TModalDispatcher = useModalDispatcher()

    const dispatcher: ReturnType<typeof useConversationDispatcher> = useConversationDispatcher(entityId)

    const closeAbandonment: RefObject<TEmptyCallback> = useRef((): void =>
        reduxBatch(
            (): void => (
                modalDispatcher.hideModal(EModalId.Participant_CoachingChatAbandonment),
                dispatcher.changeStatus(EConversationStatus.InProgress)
            )
        )
    )

    const openGuide: RefObject<(modalProps: Omit<TGuideModalProps, "createMutation">) => void> = useRef(
        (modalProps: Omit<TGuideModalProps, "mutationHook">): void =>
            modalDispatcher.showModal(EModalId.Participant_CoachingChatGuide, {
                ...modalProps,
                mutationHook: useCreateDisclaimerAwarenessMutation
            })
    )

    const closeGuide: RefObject<TEmptyCallback> = useRef((): void =>
        modalDispatcher.hideModal(EModalId.Participant_CoachingChatGuide)
    )

    const openFeedback: RefObject<(modalProps: TFeedbackModalProps) => void> = useRef(
        (modalProps: TFeedbackModalProps): void =>
            modalDispatcher.showModal<TFeedbackModalProps>(EModalId.Feedback, modalProps)
    )

    const closeFeedback: RefObject<TEmptyCallback> = useRef((): void => modalDispatcher.hideModal(EModalId.Feedback))

    const openMatrix: RefObject<TEmptyCallback> = useRef((): void =>
        modalDispatcher.showModal<TMatrixFeedbackModalProps>(EModalId.Participant_CoachingChatMatrixFeedback, {
            mutationHook: useCreateMatrixFeedbackMutation
        })
    )

    const closeMatrix: RefObject<(conversationQueryKey: QueryKey) => Promise<void>> = useRef(
        async (conversationQueryKey: QueryKey): Promise<void> => {
            const conversationDto: IConversationDto = queryClient.getQueryData(conversationQueryKey)

            if (!isEmpty(conversationDto) && eq(conversationDto.status, "completed"))
                await queryClient.setQueryData(
                    conversationQueryKey,
                    (prev: IConversationDto): IConversationDto => ({
                        ...prev,
                        is_matrix_feedback_completed: true
                    })
                )

            modalDispatcher.hideModal(EModalId.Participant_CoachingChatMatrixFeedback)
        }
    )

    return useMemo(
        (): TManager => ({
            closeAbandonment,
            openGuide,
            closeGuide,
            openFeedback,
            closeFeedback,
            openMatrix,
            closeMatrix
        }),
        []
    )
}

export { useManager as useCoachingChatModalManager }
export type { TManager as TCoachingChatModalManager }
